import { IAMCRequest } from '../utils/entities/interface';
import { Pagination } from './booking';
import { get, post } from './https';

interface AmcRequestsResponse {
  status: boolean;
  data: IAMCRequest[];
  count: number;
  pagination: Pagination;
}

const GETAmcRequests = async ({
  page = 1,
  limit = 10,
  searchQuery = '',
  sortField = '',
  sortOrder = 'DESC',
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
}): Promise<AmcRequestsResponse> => {
  // AMC requests now live in the enquiries collection (subType = AMC).
  // The old lead-based endpoint (/admin/lead/list) is empty for these.
  let url = `/api/v1/admin/enquiry-list?limit=${limit}&page=${page}&type=AMC`;

  if (searchQuery) {
    url += `&search=${searchQuery}`;
  }

  if (sortField && sortField !== 'name') {
    url += `&sortby=${sortField}`;
  }

  if (sortOrder) {
    url += `&orderby=${sortOrder === 'ASC' ? 'asc' : 'desc'}`;
  }

  const response = await get(url);

  if (response.status === 200) {
    return response.data;
  } else {
    throw new Error('Failed to fetch AMC requests');
  }
};

// Adapt the enquiry detail response (enquiry/:id) into the shape the AMC detail
// page renders. The real customer-provided AMC data lives in `amcEnquiryDetails`
// (amcenquiries collection). The legacy `amcDetails` (leads collection) is empty
// for app-created enquiries, so we derive from amcEnquiryDetails and fall back to
// amcDetails only when present.
const mapEnquiryToAmcRequest = (data: any): any => {
  const amc = data?.amcEnquiryDetails || {};
  const legacy = data?.amcDetails || {};

  // getEnquiryById returns `user_info` / `technician_info` (exclusion projection,
  // no rename). The list endpoint returns `user` / `technician`. Support both.
  const user = data?.user_info || data?.user || {};
  const tech = data?.technician_info || data?.technician || {};
  const addr = data?.addressDetails || {};

  // AC/units breakdown for the "AC Details" card — prefer the amcenquiries
  // `units`, fall back to the legacy lead `acDetails`.
  const units = Array.isArray(amc.units) ? amc.units : [];
  const legacyAc = Array.isArray(legacy.acDetails) ? legacy.acDetails : [];
  const acDetails =
    units.length > 0
      ? units.map((u: any, i: number) => ({
          _id: u._id || String(i),
          acType: u.unitType || u.category || 'Unit',
          quantity: u.quantity || 0,
        }))
      : legacyAc;

  const sumQty = (arr: any[]) =>
    arr.reduce((s: number, x: any) => s + (Number(x?.quantity) || 0), 0);

  const totalUnits =
    Number(amc.totalUnits) ||
    sumQty(units) ||
    Number(legacy.quantity) ||
    sumQty(legacyAc) ||
    Number(data?.noOfAc) ||
    0;

  const formattedAddress =
    legacy.address ||
    [addr.house, addr.street, addr.city, addr.state, addr.zipcode]
      .filter(Boolean)
      .join(', ');

  // Full per-property breakdown (address, subtype, units). A multi-site AMC
  // (noOfSites > 1) covers several properties — `amc.units`/`propertySubType`
  // above are only a flat mirror of properties[0], kept for back-compat.
  const properties = Array.isArray(amc.properties)
    ? amc.properties.map((property: any) => ({
        propertySubType: property.propertySubType || '',
        address: property.address || '',
        units: Array.isArray(property.units) ? property.units : [],
        totalUnits: Number(property.totalUnits) || 0,
      }))
    : [];

  const name = user.name || legacy.username || '';
  const phone = user.phoneNumber || legacy.phoneNumber || '';

  // A human-readable summary used in place of the old free-text "comment".
  const summaryParts = [
    amc.serviceLevel && `Service: ${amc.serviceLevel}`,
    amc.paymentPreference && `Payment: ${amc.paymentPreference}`,
    amc.propertySubType && `Type: ${amc.propertySubType}`,
    amc.includeBoiler ? 'Includes boiler' : null,
  ].filter(Boolean);

  return {
    _id: data?._id,
    createdAt: data?.createdAt,
    updatedAt: data?.updatedAt,

    // Legacy-compatible top-level fields the page reads directly.
    leadId: amc.bookingReference || legacy.leadId || data?.enquiryId || '',
    place: amc.propertyCategory || legacy.place || '',
    quantity: totalUnits,
    comment: legacy.comment || summaryParts.join(' • '),
    address: formattedAddress,
    username: name,
    phoneNumber: phone,

    userDetails: {
      _id: user.id || user._id || '',
      name,
      email: user.email || '',
      phoneNumber: phone,
      countryCode: user.countryCode || '',
      profilePhoto: user.profilePhoto || '',
      isActive: user.isActive ?? 1,
      createdAt: user.createdAt || '',
    },

    enquiry: {
      _id: data?._id,
      enquiryId: data?.enquiryId,
      type: data?.type,
      subType: data?.subType,
      status: data?.status,
      noOfAc: totalUnits,
      addressDetails: addr,
      schedule: data?.schedule || { slot: null, date: null },
    },

    acDetails,
    properties,

    // Full AMC detail retained for the quotation screen / future rich UI.
    amcEnquiryDetails: amc,

    assignedTechnician:
      tech.id || tech._id
        ? {
            _id: tech.id || tech._id,
            name: tech.name,
            phoneNumber: tech.phoneNumber,
            email: tech.email,
            countryCode: tech.countryCode,
            profilePhoto: tech.profilePhoto,
          }
        : undefined,
  };
};

const GETAmcRequestById = async (enquiryId: string) => {
  if (!enquiryId) {
    throw new Error('Enquiry id is required');
  }
  const response = await get(`/api/v1/admin/enquiry/${enquiryId}`);
  if (response?.status === 200 && response?.data?.status && response?.data?.data) {
    return mapEnquiryToAmcRequest(response.data.data);
  }
  throw new Error(response?.data?.message || 'Failed to fetch AMC request');
};

// Admin: update enquiry status (e.g. reject an AMC request)
// NOTE: the backend looks up by the human-readable `enquiryId` code, not the Mongo _id
const UpdateEnquiryStatus = async (enquiryId: string, status: string) => {
  if (!enquiryId) {
    throw new Error('Enquiry id is required');
  }
  const response = await post('/api/v1/admin/enquiry/status/manage', {
    enquiryId,
    status,
  });
  if (response?.status === 200 && response?.data?.status) {
    return response.data;
  }
  throw new Error(
    response?.data?.message || 'Failed to update enquiry status',
  );
};

// Admin: share an AMC quotation. `enquiryId` here is the Mongo _id (route param).
const SubmitAmcQuotation = async (
  enquiryId: string,
  payload: { amount: number; notes?: string; lineItems?: { label: string; amount: number }[] },
) => {
  if (!enquiryId) {
    throw new Error('Enquiry id is required');
  }
  const response = await post('/api/v1/admin/enquiry/amc/quotation', {
    enquiryId,
    ...payload,
  });
  if (response?.status === 200 && response?.data?.status) {
    return response.data;
  }
  throw new Error(response?.data?.message || 'Failed to submit AMC quotation');
};

// Admin: share a quotation for Sell Old Boiler / Expert Advice / Site Visit
// requests. Each type has its own endpoint and its own storage model (see
// enquiry.controller.js submitSellOldBoilerQuotation / submitExpertAdviceQuotation
// / submitSiteVisitQuotation) — kept separate rather than one shared path
// since the underlying enquiry data isn't generic across types.
type QuotationPayload = {
  amount: number;
  notes?: string;
  lineItems?: { label: string; amount: number }[];
};

const submitQuotation = async (
  path: string,
  enquiryId: string,
  payload: QuotationPayload,
) => {
  if (!enquiryId) {
    throw new Error('Enquiry id is required');
  }
  const response = await post(path, { enquiryId, ...payload });
  if (response?.status === 200 && response?.data?.status) {
    return response.data;
  }
  throw new Error(response?.data?.message || 'Failed to submit quotation');
};

const SubmitSellOldBoilerQuotation = (
  enquiryId: string,
  payload: QuotationPayload,
) =>
  submitQuotation(
    '/api/v1/admin/enquiry/sell-old-boiler/quotation',
    enquiryId,
    payload,
  );

const SubmitExpertAdviceQuotation = (
  enquiryId: string,
  payload: QuotationPayload,
) =>
  submitQuotation(
    '/api/v1/admin/enquiry/expert-advice/quotation',
    enquiryId,
    payload,
  );

const SubmitSiteVisitQuotation = (
  enquiryId: string,
  payload: QuotationPayload,
) =>
  submitQuotation(
    '/api/v1/admin/enquiry/site-visit/quotation',
    enquiryId,
    payload,
  );

export {
  GETAmcRequests,
  GETAmcRequestById,
  UpdateEnquiryStatus,
  SubmitAmcQuotation,
  SubmitSellOldBoilerQuotation,
  SubmitExpertAdviceQuotation,
  SubmitSiteVisitQuotation,
};
